/**
 * Dimensions follow the walls they measure: when a wall sketch changes, a
 * dimension end that sat on the wall skin moves with it — pure geometry.
 *
 * The skin's corners and faces carry no ids, so the attachment is read from
 * the geometry BEFORE the change: the wall lines the end lies on (a side face
 * of an edge, offset by that side's thickness, or an edge's end cap). On one
 * face the end keeps its fraction along the edge; on a cap its distance from
 * the node; on two crossing lines (a corner) it stays where they cross. Ends
 * off the walls, on curved walls, or on walls that are gone don't move.
 */

import type { SkeletonEdgeData, SkeletonNodeData } from '@pointorama/potree-shared';

type Point = { x: number; y: number };

/** A wall sketch's data at one moment. */
export interface WallGeometry {
  nodes: readonly SkeletonNodeData[];
  edges: readonly SkeletonEdgeData[];
}

/** One straight wall line: a side face (`L` / `R`) or an end cap (`S` / `E`) of an edge. */
interface WallLine {
  /** Where it starts: the face's start (or the cap's node). */
  origin: Point;
  /** Unit direction. */
  dir: Point;
  /** The edge length (faces scale their attachment with it). */
  length: number;
  face: boolean;
}

/** How close (m) a point must be to a wall line to sit on it. */
const ON_LINE = 1e-3;
/** The skin's thickness where a side has none set (as the skin draws it). */
const DEFAULT_SIDE = 0.1;

/** Every straight wall line of a sketch, keyed `<edge id>:<L|R|S|E>` (outline and curved edges skipped). */
function wallLines(geometry: WallGeometry): Map<string, WallLine> {
  const nodes = new Map(geometry.nodes.map((n) => [n.id, n.position]));
  const lines = new Map<string, WallLine>();
  for (const edge of geometry.edges) {
    if (edge.role === 'OUTLINE' || Math.abs(edge.bulge ?? 0) > 1e-6) continue;
    const a = nodes.get(edge.startNodeId);
    const b = nodes.get(edge.endNodeId);
    if (!a || !b) continue;
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    if (length < 1e-9) continue;
    const dir = { x: (b.x - a.x) / length, y: (b.y - a.y) / length };
    const left = { x: -dir.y, y: dir.x };
    const leftSize = edge.leftSize || DEFAULT_SIDE;
    const rightSize = edge.rightSize || DEFAULT_SIDE;
    lines.set(`${edge.id}:L`, { origin: { x: a.x + left.x * leftSize, y: a.y + left.y * leftSize }, dir, length, face: true });
    lines.set(`${edge.id}:R`, { origin: { x: a.x - left.x * rightSize, y: a.y - left.y * rightSize }, dir, length, face: true });
    lines.set(`${edge.id}:S`, { origin: { x: a.x, y: a.y }, dir: left, length, face: false });
    lines.set(`${edge.id}:E`, { origin: { x: b.x, y: b.y }, dir: left, length, face: false });
  }
  return lines;
}

const along = (p: Point, line: WallLine): number => (p.x - line.origin.x) * line.dir.x + (p.y - line.origin.y) * line.dir.y;

const distanceTo = (p: Point, line: WallLine): number => Math.abs((p.x - line.origin.x) * line.dir.y - (p.y - line.origin.y) * line.dir.x);

function intersect(a: WallLine, b: WallLine): Point | null {
  const denom = a.dir.x * b.dir.y - a.dir.y * b.dir.x;
  if (Math.abs(denom) < 1e-9) return null;
  const t = ((b.origin.x - a.origin.x) * b.dir.y - (b.origin.y - a.origin.y) * b.dir.x) / denom;
  return { x: a.origin.x + a.dir.x * t, y: a.origin.y + a.dir.y * t };
}

/**
 * Where `p` goes when the walls change from `before` to `after` — null when
 * it didn't sit on a wall line (or its lines are gone / curved now).
 */
export function followWalls(p: Point, before: WallGeometry, after: WallGeometry): Point | null {
  const old = wallLines(before);
  // The lines through `p`, faces first (a corner is two faces when it can be).
  const on = [...old.entries()].filter(([, line]) => distanceTo(p, line) <= ON_LINE).sort(([, a], [, b]) => Number(b.face) - Number(a.face));
  if (on.length === 0) return null;
  const now = wallLines(after);

  // A corner: two crossing lines.
  const [firstKey, first] = on[0];
  const crossing = on.find(([, line]) => Math.abs(first.dir.x * line.dir.y - first.dir.y * line.dir.x) > 1e-6);
  if (crossing) {
    const a = now.get(firstKey);
    const b = now.get(crossing[0]);
    return a && b ? intersect(a, b) : null;
  }

  // One line: the same place along it (a face scales with its edge's length).
  const line = now.get(firstKey);
  if (!line) return null;
  const distance = first.face ? (along(p, first) / first.length) * line.length : along(p, first);
  return { x: line.origin.x + line.dir.x * distance, y: line.origin.y + line.dir.y * distance };
}
